import FullScreenPanel from "app/ui/FullScreenPanel";
import { useCallback, useEffect } from "react";
import TutorialLevelList from "./TutorialLevelList";
import type { TutorialLevel } from "./tutorial-data";
import { TutorialAction, useTutorialActionDispatch } from "./useTutorialState";
import MenuButton from "app/ui/MenuButton";
import SegmentedProgressBar from "app/ui/SegmentedProgressBar";
import useGameState from "app/game/useGameState";
import TUTORIAL_LEVEL_ORDER from "./tutorial-level-order";

interface TutorialProgressPanelProps {
  onLevelStarted: () => void;
  onSkip: () => void;
  onAllFinished: () => void;
}

// This screen is used to guide new players between tutorial levels during the
// onboarding sequence.
export default function TutorialProgressPanel({
  onLevelStarted,
  onSkip,
  onAllFinished,
}: TutorialProgressPanelProps) {
  const tutorialDispatch = useTutorialActionDispatch();
  const { completedTutorialLevels } = useGameState();
  const numLevelsCompleted = completedTutorialLevels?.size ?? 0;
  const isFinished = numLevelsCompleted >= TUTORIAL_LEVEL_ORDER.length;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleLevelSelected = useCallback(
    (levelData: TutorialLevel) => {
      onLevelStarted();
      tutorialDispatch({
        type: TutorialAction.START_TUTORIAL_LEVEL,
        payload: { levelData },
      });
    },
    [onLevelStarted, tutorialDispatch],
  );

  return (
    <FullScreenPanel
      heading="Tutorial Progress"
      headingClassName="px-9 pb-5"
      panelClassName="md:h-auto lg:h-auto shrink"
      childDivClassName="flex-col justify-start items-center gap-5 h-full pb-5"
      hideCloseButton
    >
      {isFinished ? <p>All levels complete!</p> : <p>Keep going!</p>}
      <SegmentedProgressBar
        className="w-[90%] max-w-75"
        numSteps={TUTORIAL_LEVEL_ORDER.length}
        currentStep={numLevelsCompleted}
      />
      <TutorialLevelList
        className="md:px-8"
        onLevelSelected={handleLevelSelected}
        isInOnboardingFlow={true}
      />
      <div className="grow" />
      <div>
        {isFinished ? (
          <MenuButton className="max-w-none px-5" onClick={onAllFinished}>
            Play Pixellence
          </MenuButton>
        ) : (
          <MenuButton variant="small" className="text-lg" onClick={onSkip}>
            Skip Tutorials
          </MenuButton>
        )}
      </div>
    </FullScreenPanel>
  );
}
